import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@timely/ui';
import { supabase } from '../lib/supabaseClient';
import { publicConfig } from '../config/env';
import { callbackUrl } from '../auth/session';
import {
    CHECK_YOUR_EMAIL,
    isEmailShaped,
    normaliseEmail,
    passwordProblems,
    signUpError,
} from '../auth/authErrors';
import { AuthLayout, AuthLink, Field, FormNotice, PasswordRules } from './AuthLayout';

/**
 * Creating an account.
 *
 * Two things this screen refuses to do:
 *
 *   It does not say whether the address is already registered. Success and
 *   "that address is taken" produce the same sentence, because the difference
 *   between them is a way of asking our server who banks with which supplier.
 *
 *   It does not block Gmail or Outlook. Plenty of real businesses run on them;
 *   a verified address is the requirement, not a corporate domain.
 */
export function SignUp() {
    const [params] = useSearchParams();
    const inviteToken = params.get('invite');

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    const [touched, setTouched] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [sent, setSent] = useState(false);
    const [busy, setBusy] = useState(false);

    const problems = passwordProblems(password);
    const emailLooksWrong = touched && email.length > 0 && !isEmailShaped(email);

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (busy) return;
        setTouched(true);
        setError(null);

        if (!isEmailShaped(email)) {
            setError('That does not look like an email address.');
            return;
        }
        if (problems.length > 0) {
            setError('That password does not meet the rules below.');
            return;
        }

        setBusy(true);
        // The link in the email comes back to this app and nowhere else: the
        // origin is ours, not anything the page was handed.
        const redirectTo = callbackUrl(
            publicConfig().appUrl,
            '/auth/callback',
            inviteToken ? `/invite/${inviteToken}` : null,
        );
        const { error: authError } = await supabase().auth.signUp({
            email: normaliseEmail(email),
            password,
            options: {
                emailRedirectTo: redirectTo,
                data: name.trim() ? { name: name.trim() } : {},
            },
        });
        setBusy(false);

        const message = signUpError(authError);
        if (message) {
            setError(message);
            return;
        }
        // Reached whether the address was new or already registered.
        setSent(true);
    };

    if (sent) {
        return (
            <AuthLayout title="Check your email" lede={CHECK_YOUR_EMAIL}>
                <p className="text-body text-label-2 leading-relaxed">
                    Open the link to confirm your address, then sign in with your password and set up
                    your company. If nothing arrives in a few minutes, look in your spam folder, or{' '}
                    <Link to="/verify-email" className="text-accent-text font-semibold hover:underline">send a new link</Link>.
                </p>
                <div className="mt-6">
                    <AuthLink to="/login">Back to sign in</AuthLink>
                </div>
            </AuthLayout>
        );
    }

    return (
        <AuthLayout
            title="Create your account"
            lede={inviteToken
                ? 'You have been invited to a company. Create an account with the address the invitation was sent to.'
                : 'One account, then you set up your company.'}
            footer={<>Already have an account? <AuthLink to="/login">Sign in</AuthLink></>}
        >
            <form onSubmit={submit} noValidate>
                {error && <FormNotice tone="error">{error}</FormNotice>}

                <Field large
                    label="Your name"
                    name="name"
                    autoComplete="name"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    hint="Shown to your colleagues on the work you record."
                />
                <Field large
                    label="Work email"
                    name="email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    autoCapitalize="off"
                    autoCorrect="off"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    onBlur={() => setTouched(true)}
                    error={emailLooksWrong ? 'That does not look like an email address.' : null}
                    required
                />
                <Field large
                    label="Password"
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    hint={<PasswordRules problems={problems} />}
                />

                <Button type="submit" variant="primary" size="lg" className="w-full mt-2" busy={busy}>
                    {busy ? 'Creating your account…' : 'Create account'}
                </Button>
            </form>
        </AuthLayout>
    );
}
